import { useEffect, useMemo, useState } from 'react';
import { DeviceInfo, useBridgeApi } from './useBridgeApi';
import { datadogLogs } from '@datadog/browser-logs';

// This object is returned in DEV when no device info is present

export type DeviceModel =
  | 'DEV'
  | 'Zettle Terminal'
  | 'Saturn1000F2'
  | 'iPhone'
  | 'iPhone 13'
  | 'iPhone 14'
  | 'iPhone 15'
  | 'iPhone 12'
  | 'S1F4 PRO';

const HANDHELD_MODELS: DeviceModel[] = [
  'Zettle Terminal',
  'Saturn1000F2',
  'S1F4 PRO',
  'iPhone 12',
  'iPhone 13',
  'iPhone 14',
  'iPhone 15',
  'iPhone',
];

const PAYMENT_METHOD_MAP: Record<DeviceModel, string | null> = {
  Saturn1000F2: 'ADYEN',
  'S1F4 PRO': 'ADYEN',
  'Zettle Terminal': 'IZETTLE',
  'iPhone 12': null,
  'iPhone 13': null,
  'iPhone 14': null,
  'iPhone 15': null,
  iPhone: null,
  DEV: 'ADYEN',
};

/**
 * This is the return type of this hook. It is an extension of the
 * DeviceInfo interface including convinence properties provided by the hook
 */
interface ExtendedDeviceInfo extends DeviceInfo {
  isHandheldTerminal: boolean;
  specificPaymentMethod: string | null;
  isZettleTerminal: boolean;
  isAdyenTerminal: boolean;
  isPhone: boolean;
}

// This will never change, so we can cache it
let cachedDeviceInfo: DeviceInfo | null = null;

const isStorybook = !!process.env.STORYBOOK_MODE;

/**
 * This hook aims to provide a more convenient way to access device info.
 *
 * It adds some inferred metadata about the device, such as whether it's a handheld terminal
 * and if there's a specific primary payment method that we can assume to always use for it.
 */
export const useDeviceInfo = () => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const bridgeApi = useBridgeApi();

  useEffect(() => {
    if (cachedDeviceInfo) {
      setDeviceInfo(cachedDeviceInfo);
      return;
    }

    bridgeApi.getDeviceInfo().then((info) => {
      // If data is broken, dont use it
      // This is due to an issue in the native layer where we return
      // { requestId: string } (without any data) instead of null
      if (!info?.model) {
        return;
      }

      cachedDeviceInfo = info;
      setDeviceInfo(info);
      datadogLogs.setGlobalContextProperty('deviceInfo', info);
    });
  }, []);

  const result = useMemo(() => {
    if (!deviceInfo) {
      const DEV_PAYLOAD: ExtendedDeviceInfo = {
        device: 'DEV',
        deviceId: 'DEV',
        deviceType: 'DEV',
        display: 'DEV',
        deviceName: 'DEV_MOCK',
        model: 'DEV',
        serialNumber: 'DEV',
        brand: 'DEV',
        systemName: 'Android',
        systemVersion: '0.0.0',
        isAdyenTerminal: false,
        isZettleTerminal: false,
        isHandheldTerminal: false,
        specificPaymentMethod: null,
        isPhone: false,
        hasNotch: false,
        isTablet: true,
      };

      if (window.__DEV__) {
        return DEV_PAYLOAD;
      }

      if (isStorybook) {
        return DEV_PAYLOAD;
      }

      return null;
    }

    const isHandheldTerminal =
      HANDHELD_MODELS.includes(deviceInfo.model) || deviceInfo.deviceType === 'Handset';

    const specificPaymentMethod = PAYMENT_METHOD_MAP[deviceInfo.model] || null;

    return {
      ...deviceInfo,
      isHandheldTerminal,
      specificPaymentMethod,

      isPhone: deviceInfo.model.includes('iPhone'),

      // We will have so much use of these variables, so lets just expose them
      isZettleTerminal: specificPaymentMethod === 'IZETTLE',
      isAdyenTerminal: specificPaymentMethod === 'ADYEN',
    } as ExtendedDeviceInfo;
  }, [deviceInfo]);

  return result;
};
